import React from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { X, Award, User } from 'lucide-react';
import { useArticleAuthors, useArticleExperts } from '@/hooks/useArticleAuthors';
import type { ArticleAuthor } from '@/types/articles';

interface ArticleAuthorSelectProps {
  brandId: string | undefined;
  value: string | null;
  onChange: (value: string | null) => void;
  placeholder?: string;
  expertsOnly?: boolean;
  disabled?: boolean;
}

export function ArticleAuthorSelect({
  brandId,
  value,
  onChange,
  placeholder = 'Select author',
  expertsOnly = false,
  disabled = false,
}: ArticleAuthorSelectProps) {
  const { data: allAuthors = [] } = useArticleAuthors(brandId);
  const { data: experts = [] } = useArticleExperts(brandId);

  const authors = expertsOnly ? experts : allAuthors;
  const selectedAuthor = authors.find(a => a.id === value);

  const getInitials = (name: string) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  if (!brandId) return null;

  return (
    <Select
      value={value || ''}
      onValueChange={(v) => onChange(v || null)}
      disabled={disabled}
    >
      <SelectTrigger>
        <SelectValue placeholder={placeholder}>
          {selectedAuthor && (
            <div className="flex items-center gap-2">
              <Avatar className="h-5 w-5">
                <AvatarImage src={selectedAuthor.avatar_url || undefined} />
                <AvatarFallback className="text-xs">
                  {getInitials(selectedAuthor.name)}
                </AvatarFallback>
              </Avatar>
              <span>{selectedAuthor.name}</span>
            </div>
          )}
        </SelectValue>
      </SelectTrigger>
      <SelectContent>
        {authors.length === 0 ? (
          <div className="py-4 text-center text-sm text-muted-foreground">
            {expertsOnly ? 'No experts found' : 'No authors found'}
          </div>
        ) : (
          authors.map((author) => (
            <SelectItem key={author.id} value={author.id}>
              <div className="flex items-center gap-2">
                <Avatar className="h-6 w-6">
                  <AvatarImage src={author.avatar_url || undefined} />
                  <AvatarFallback className="text-xs">
                    {getInitials(author.name)}
                  </AvatarFallback>
                </Avatar>
                <div className="flex flex-col">
                  <span>{author.name}</span>
                  {author.title && (
                    <span className="text-xs text-muted-foreground">{author.title}</span>
                  )}
                </div>
                {author.is_expert && (
                  <Award className="h-3 w-3 text-amber-500 ml-auto" />
                )}
              </div>
            </SelectItem>
          ))
        )}
      </SelectContent>
    </Select>
  );
}

interface MultiAuthorSelectProps {
  brandId: string | undefined;
  values: string[];
  onChange: (values: string[]) => void;
  placeholder?: string;
  expertsOnly?: boolean;
  disabled?: boolean;
}

export function MultiAuthorSelect({
  brandId,
  values,
  onChange,
  placeholder = 'Select authors',
  expertsOnly = false,
  disabled = false,
}: MultiAuthorSelectProps) {
  const { data: allAuthors = [] } = useArticleAuthors(brandId);
  const { data: experts = [] } = useArticleExperts(brandId);

  const authors = expertsOnly ? experts : allAuthors;
  const selectedAuthors = authors.filter(a => values.includes(a.id));
  const availableAuthors = authors.filter(a => !values.includes(a.id));

  const getInitials = (name: string) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const handleAdd = (authorId: string) => {
    onChange([...values, authorId]);
  };

  const handleRemove = (authorId: string) => {
    onChange(values.filter(id => id !== authorId));
  };

  if (!brandId) return null;

  return (
    <div className="space-y-2">
      {/* Selected authors */}
      {selectedAuthors.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {selectedAuthors.map((author) => (
            <Badge key={author.id} variant="secondary" className="gap-1 pr-1">
              <Avatar className="h-4 w-4">
                <AvatarImage src={author.avatar_url || undefined} />
                <AvatarFallback className="text-[10px]">
                  {getInitials(author.name)}
                </AvatarFallback>
              </Avatar>
              <span>{author.name}</span>
              {author.is_expert && <Award className="h-3 w-3 text-amber-500" />}
              <Button
                variant="ghost"
                size="icon"
                className="h-4 w-4 p-0 hover:bg-transparent"
                onClick={() => handleRemove(author.id)}
                disabled={disabled}
              >
                <X className="h-3 w-3" />
              </Button>
            </Badge>
          ))}
        </div>
      )}

      {/* Add dropdown */}
      {availableAuthors.length > 0 && (
        <Select
          value=""
          onValueChange={handleAdd}
          disabled={disabled}
        >
          <SelectTrigger className="w-full">
            <SelectValue placeholder={placeholder} />
          </SelectTrigger>
          <SelectContent>
            {availableAuthors.map((author) => (
              <SelectItem key={author.id} value={author.id}>
                <div className="flex items-center gap-2">
                  <Avatar className="h-6 w-6">
                    <AvatarImage src={author.avatar_url || undefined} />
                    <AvatarFallback className="text-xs">
                      {getInitials(author.name)}
                    </AvatarFallback>
                  </Avatar>
                  <span>{author.name}</span>
                  {author.is_expert && (
                    <Award className="h-3 w-3 text-amber-500 ml-auto" />
                  )}
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      {authors.length === 0 && (
        <p className="text-sm text-muted-foreground">
          {expertsOnly ? 'No experts available' : 'No authors available'}
        </p>
      )}
    </div>
  );
}
